import { graphql } from 'gatsby';
import { Layout, Seo } from '@layout';
import { Box } from '@components/Box';
import { BabyRegistry } from '@sections/shared/BabyRegistry';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useOnScreenTelemetry } from '@hooks/useOnScreenTelemetry';
import { AllEventTypes, EventType } from '@sections/find/HeroSection/HeroSection.types';
import { HeroSection, BlogCarouselV2, CelebratingCoupleSection, NavigationSection, BrandsSection, BlogPropsV2 } from '@sections/find';
import { useTranslation } from 'react-i18next';

const BABY_REGISTRY_SECTION_ID = 'FindAnEvent_Baby_Registry_Section';

interface FindCouplePageData {
  weddingGuestsBlogs: {
    blogs: ReadonlyArray<BlogPropsV2>;
  };
  babyGuestsBlogs: {
    blogs: ReadonlyArray<BlogPropsV2>;
  };
}

export const query = graphql`
  {
    weddingGuestsBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
        categories {
          id
          name
          link
        }
      }
    }
    babyGuestsBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
        categories {
          id
          name
          link
        }
      }
    }
  }
`;

const FindCouple = ({ data }: { data: FindCouplePageData }) => {
  const [eventType, setEventType] = useState<AllEventTypes>('');
  const babyRegistrySectionRef = useRef<HTMLDivElement>(null);

  useOnScreenTelemetry(babyRegistrySectionRef, BABY_REGISTRY_SECTION_ID);

  const blogs = useMemo(
    () => (eventType === EventType.babyRegistry ? data.babyGuestsBlogs.blogs : data.weddingGuestsBlogs.blogs),
    [data.babyGuestsBlogs.blogs, data.weddingGuestsBlogs.blogs, eventType]
  );

  const handleOnChangeEventType = useCallback((eventType: AllEventTypes) => setEventType(eventType), []);

  const { t } = useTranslation('findAnEvent');
  const findACouplePageData = t('findACouple');

  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="newPurple5"
      transparentHeaderButtonVariant="primary"
      transparentHeaderLinkVariant="navLink"
      page="find"
    >
      <Seo title={findACouplePageData.seo.title} description={findACouplePageData.seo.content} canonicalURL={findACouplePageData.seo.url} />
      <HeroSection onChangeEventType={handleOnChangeEventType} eventType={eventType} />
      <BlogCarouselV2
        key={eventType}
        sectionId="FindAnEvent_Blog_Section"
        title={findACouplePageData.blogTitle}
        blogs={blogs.slice(0, 9)}
      />
      <CelebratingCoupleSection />
      <NavigationSection />
      <BrandsSection />
      <Box as="section" id={BABY_REGISTRY_SECTION_ID} ref={babyRegistrySectionRef}>
        <BabyRegistry page="find" />
      </Box>
    </Layout>
  );
};

export default FindCouple;
